import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import { supabase } from '../../utils/supabase'
import posthog, { isPostHogConfigured } from '../../posthog'
import { convertToUtcTimestamps, getAppointmentDuration, SLOT_INTERVAL_MINUTES } from '../../utils/appointmentUtils'
import { getFullName } from '../../utils/clientUtils'
import { getCurrencySymbol } from '../../utils/currencyUtils'
import DropdownCalendar from '../ui/DropdownCalendar'
import TimeListPicker from '../ui/TimeListPicker'
import OverlayScrollThumb from '../ui/OverlayScrollThumb'
import { L, Icon, LavSectionTitle, useLavMobile } from './appointmentLavenderFields'
import { v4 as uuidv4 } from 'uuid'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * RecurringAppointmentsOverview — "Series check · resolve conflicts" (step 3).
 *
 * Preview a generated recurring series, surface per-occurrence conflicts, and
 * resolve each one inline (Reschedule / Skip / Allow overlap) before booking.
 *
 * Behaviour:
 *   • Occurrences are seeded from the parent ({base + freq/interval/count}).
 *   • Conflicts are computed against (1) the merchant's existing appointments in
 *     the series window and (2) other occurrences in this list (siblings).
 *   • Each conflicting visit must be resolved — reschedule it, Skip it (same as
 *     the old delete: excluded from booking, reversible via Undo), or Allow the
 *     overlap (double-booking is permitted by design). Book is disabled until
 *     every conflict is resolved.
 *   • "Book" inserts all active (non-skipped) rows in a single batched INSERT
 *     with a shared recurrence_group_id.
 *
 * Props:
 *   merchant, client — pre-resolved objects
 *   appointments: the whole visit — [{ pet, service, addOns, employee, … }], one
 *     entry per pet, each keeping its own service, add-ons and (possibly
 *     customised) duration. Every occurrence recreates this exact visit.
 *   notes: appointment notes carried onto every occurrence
 *   initialOccurrences: [{ date: 'YYYY-MM-DD', time: 'HH:mm' }]
 *   onBack, onBook (called after successful insert)
 *   isMobile, saveActionRef  (mobile header wires "Book" through this ref)
 */
export default function RecurringAppointmentsOverview({
	merchant,
	client,
	appointments = [],
	notes = '',
	initialOccurrences = [],
	onBack,
	onBook,
	isMobile = false,
	saveActionRef = null,
}) {
	const tz = merchant?.timezone || 'UTC'

	// Inject Outfit font (lavender design typeface)
	useEffect(() => {
		const id = 'outfit-font-link'
		if (!document.getElementById(id)) {
			const link = document.createElement('link')
			link.id = id
			link.rel = 'stylesheet'
			link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap'
			document.head.appendChild(link)
		}
	}, [])

	// One occurrence = the whole visit, so its length is every pet's duration
	// back to back. getAppointmentDuration honours a custom length when the
	// merchant overrode it, and otherwise sums service + add-ons.
	const duration = useMemo(
		() => appointments.reduce((sum, apt) => sum + getAppointmentDuration(apt), 0),
		[appointments]
	)

	// Every groomer taking part in the visit — the conflict window covers all.
	const employeeIds = useMemo(
		() => [...new Set(appointments.map((a) => a.employee?.id).filter(Boolean))],
		[appointments]
	)

	// Stamp each row with a stable tempId. origDate/origTime track "moved" state;
	// allowed/removed hold the per-occurrence resolution.
	const [occurrences, setOccurrences] = useState(() =>
		initialOccurrences.map((o) => ({
			tempId: uuidv4(), date: o.date, time: o.time,
			origDate: o.date, origTime: o.time, allowed: false, removed: false,
		}))
	)
	const [expandedKeys, setExpandedKeys] = useState([])

	// Conflicts fetched from DB (existing appointments overlapping the series window).
	const [externalAppts, setExternalAppts] = useState([])
	const [loadingConflicts, setLoadingConflicts] = useState(false)
	const [booking, setBooking] = useState(false)
	const [error, setError] = useState('')

	// Fetch existing appointments inside [first occurrence start, last occurrence end].
	const reloadConflicts = useCallback(async () => {
		if (!merchant?.id || occurrences.length === 0 || employeeIds.length === 0 || duration <= 0) return
		setLoadingConflicts(true)
		try {
			const ranges = occurrences
				.filter((o) => o.date && o.time)
				.map((o) => convertToUtcTimestamps(o.date, o.time, tz, duration))
			if (ranges.length === 0) {
				setExternalAppts([])
				return
			}
			const minStart = ranges.reduce((m, r) => (r.utcStart < m ? r.utcStart : m), ranges[0].utcStart)
			const maxEnd = ranges.reduce((m, r) => (r.utcEnd > m ? r.utcEnd : m), ranges[0].utcEnd)

			// One query for the whole window; overlap math happens client-side per row.
			const { data, error: queryErr } = await supabase
				.from('appointments')
				.select('id, start_ts, end_ts, employee_id, status, customer_name')
				.eq('merchant_id', merchant.id)
				.in('employee_id', employeeIds)
				.neq('status', 'cancelled')
				.lt('start_ts', maxEnd)
				.gt('end_ts', minStart)

			if (queryErr) throw queryErr
			setExternalAppts(data || [])
		} catch (e) {
			console.error('Failed to load conflict window:', e)
			// Soft-fail: we just won't show overlap markers. Booking still works.
			setExternalAppts([])
		} finally {
			setLoadingConflicts(false)
		}
	}, [merchant?.id, employeeIds, duration, occurrences, tz])

	useEffect(() => {
		reloadConflicts()
	}, [reloadConflicts])

	// Compute conflicts per occurrence — overlapping external appointment ids +
	// sibling tempIds. Skipped (removed) rows neither conflict nor cause conflicts.
	const conflictsByOcc = useMemo(() => {
		const map = {}
		const occStarts = occurrences.map((o) => {
			if (o.removed || !o.date || !o.time) return null
			const { utcStart, utcEnd } = convertToUtcTimestamps(o.date, o.time, tz, duration)
			return { tempId: o.tempId, utcStart, utcEnd }
		})
		for (let i = 0; i < occurrences.length; i++) {
			const me = occStarts[i]
			if (!me) { map[occurrences[i].tempId] = { external: [], siblings: [] }; continue }
			const external = externalAppts
				.filter((a) => a.start_ts < me.utcEnd && a.end_ts > me.utcStart)
				.map((a) => a.id)
			const siblings = occStarts
				.filter((other, j) => j !== i && other && other.utcStart < me.utcEnd && other.utcEnd > me.utcStart)
				.map((other) => other.tempId)
			map[occurrences[i].tempId] = { external, siblings }
		}
		return map
	}, [occurrences, externalAppts, duration, tz])

	// Per-occurrence status derived from conflicts + resolution flags.
	const statusByOcc = useMemo(() => {
		const map = {}
		occurrences.forEach((o) => {
			const c = conflictsByOcc[o.tempId] || { external: [], siblings: [] }
			const hasConflict = !o.removed && (c.external.length > 0 || c.siblings.length > 0)
			map[o.tempId] = {
				hasConflict,
				unresolved: hasConflict && !o.allowed,
				allowed: hasConflict && o.allowed,
			}
		})
		return map
	}, [occurrences, conflictsByOcc])

	const conflictCount = useMemo(
		() => occurrences.reduce((s, o) => s + (statusByOcc[o.tempId]?.unresolved ? 1 : 0), 0),
		[occurrences, statusByOcc]
	)
	const activeVisits = useMemo(() => occurrences.filter((o) => !o.removed), [occurrences])
	const allowedCount = useMemo(
		() => occurrences.reduce((s, o) => s + (statusByOcc[o.tempId]?.allowed ? 1 : 0), 0),
		[occurrences, statusByOcc]
	)
	const skippedCount = occurrences.length - activeVisits.length

	// --- per-occurrence mutations ---
	const patch = (tempId, p) => setOccurrences((prev) => prev.map((o) => (o.tempId === tempId ? { ...o, ...p } : o)))
	const handleDateChange = (tempId, v) => patch(tempId, { date: v })
	const handleTimeChange = (tempId, v) => patch(tempId, { time: v })
	const handleSkip = (tempId) => { patch(tempId, { removed: true }); setExpandedKeys((k) => k.filter((x) => x !== tempId)) }
	const handleAllow = (tempId) => patch(tempId, { allowed: true })
	const handleUndo = (tempId) => patch(tempId, { allowed: false, removed: false })
	const toggleExpand = (tempId) => setExpandedKeys((k) => (k.includes(tempId) ? k.filter((x) => x !== tempId) : [...k, tempId]))

	// Build readable conflict lines for a row.
	const conflictLinesFor = (o) => {
		const c = conflictsByOcc[o.tempId] || { external: [], siblings: [] }
		const lines = []
		c.external.forEach((id) => {
			const a = externalAppts.find((x) => x.id === id)
			if (!a) return
			const range = `${dayjs(a.start_ts).tz(tz).format('h:mm A')} – ${dayjs(a.end_ts).tz(tz).format('h:mm A')}`
			lines.push(`${a.customer_name || 'Existing booking'} · ${range}`)
		})
		if (c.siblings.length > 0) {
			lines.push(`${c.siblings.length} other visit${c.siblings.length === 1 ? '' : 's'} in this series`)
		}
		return lines
	}

	const handleBook = useCallback(async () => {
		const active = occurrences.filter((o) => !o.removed && o.date && o.time)
		if (active.length === 0) {
			setError('Please keep at least one occurrence.')
			return
		}
		if (conflictCount > 0) {
			setError(`${conflictCount} visit${conflictCount === 1 ? '' : 's'} still ${conflictCount === 1 ? 'has' : 'have'} an unresolved conflict.`)
			return
		}

		setBooking(true)
		setError('')
		try {
			const recurrenceGroupId = uuidv4()
			const multiPet = appointments.length > 1

			// Each occurrence rebuilds the whole visit: one row per pet, run back
			// to back from the occurrence's start, sharing a group_id so the
			// calendar renders them as one multi-pet visit (as the one-off flow
			// does). All rows across all occurrences share recurrence_group_id.
			const rows = active
				.slice()
				.sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`))
				.flatMap((o) => {
					const groupId = multiPet ? uuidv4() : null
					let cursor = 0
					return appointments.map((apt, i) => {
						const petMins = getAppointmentDuration(apt)
						const startMins = cursor
						cursor += petMins
						const { utcStart, utcEnd } = convertToUtcTimestamps(
							o.date,
							dayjs.tz(`${o.date} ${o.time}`, tz).add(startMins, 'minute').format('HH:mm'),
							tz,
							petMins
						)
						const addOnsData = (apt.addOns || []).map((a) => ({
							service_id: a.id,
							name: a.name,
							price: a.price,
							duration_minutes: a.duration_minutes || 0,
						}))
						return {
							merchant_id: merchant.id,
							service_id: apt.service?.id,
							employee_id: apt.employee?.id || null,
							user_id: client.id,
							pet_id: apt.pet?.id,
							start_ts: utcStart,
							end_ts: utcEnd,
							status: 'booked',
							customer_name: getFullName(client),
							customer_email: client.email || null,
							phone_number: client.phone_number || null,
							add_ons: addOnsData.length > 0 ? addOnsData : null,
							group_id: groupId,
							is_group_primary: multiPet ? i === 0 : false,
							comments: notes.trim() || null,
							recurrence_group_id: recurrenceGroupId,
						}
					})
				})

			// Single batched INSERT — atomic on the DB side; reminders are auto-
			// created per row by the sync_appointment_reminders trigger.
			const { data: inserted, error: insertErr } = await supabase
				.from('appointments')
				.insert(rows)
				.select('id, start_ts')

			if (insertErr) throw insertErr

			// One agreement email for the whole series — anchored to the first
			// inserted appointment so the client doesn't get N copies.
			if (client?.id && client?.email && inserted && inserted.length > 0) {
				const firstId = inserted[0].id
				supabase.functions
					.invoke('send-agreement', {
						body: {
							clientId: client.id,
							merchantId: merchant.id,
							channel: 'email',
							appointmentId: firstId,
						},
					})
					.catch((err) => console.error('Failed to send agreement email:', err))
			}

			if (isPostHogConfigured) {
				posthog.capture('recurring_appointments_created', {
					occurrence_count: active.length,
					appointment_count: rows.length,
					is_group_booking: multiPet,
					allowed_overlap_count: allowedCount,
					skipped_occurrence_count: skippedCount,
				})
			}
			onBook?.()
		} catch (e) {
			console.error('Recurring booking error:', e)
			setError(e.message || 'Failed to create recurring appointments. Please try again.')
		} finally {
			setBooking(false)
		}
	}, [occurrences, conflictCount, merchant, client, appointments, notes, tz, onBook])

	// Wire mobile header Book button through saveActionRef (same pattern as siblings).
	useEffect(() => {
		if (saveActionRef) saveActionRef.current = handleBook
		return () => {
			if (saveActionRef) saveActionRef.current = null
		}
	}, [saveActionRef, handleBook])

	const pricePerOcc = appointments.reduce(
		(sum, apt) => sum + (apt.service?.price || 0) + (apt.addOns || []).reduce((s, a) => s + (a.price || 0), 0),
		0
	)
	const cs = getCurrencySymbol(merchant?.currency)
	const liveTotalPrice = pricePerOcc * activeVisits.length

	const okParts = [
		allowedCount ? `${allowedCount} overlap${allowedCount > 1 ? 's' : ''} allowed` : null,
		skippedCount ? `${skippedCount} skipped` : null,
	].filter(Boolean)

	const canBook = conflictCount === 0 && activeVisits.length > 0 && !booking
	const petNames = appointments.map((a) => a.pet?.name).filter(Boolean).join(' & ')

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', fontFamily: L.font }}>
			{/* Scrollable content */}
			<Box className="lav-overlay-scroll" sx={{
				display: 'flex', flexDirection: 'column', gap: '18px', flex: 1,
				overflow: 'auto', overflowX: 'hidden',
				p: '4px 16px 0',
			}}>
				<OverlayScrollThumb />
				{/* Context line — the whole visit, one line per pet */}
				<Box>
					<Typography sx={{ fontSize: '15px', fontWeight: 750, color: L.text, fontFamily: L.font }}>
						{petNames || 'Visit'}
						<Box component="span" sx={{ color: L.muted, fontWeight: 650 }}>
							{` · ${duration} min`}
						</Box>
					</Typography>
					{appointments.map((apt, i) => (
						<Typography
							key={apt.pet?.id || i}
							sx={{ fontSize: '12px', color: L.muted, mt: '2px', fontFamily: L.font }}
						>
							{appointments.length > 1 ? `${apt.pet?.name} · ` : ''}
							{apt.service?.name}
							{(apt.addOns || []).length ? ` + ${apt.addOns.map((a) => a.name).join(', ')}` : ''}
							{apt.employee ? ` · ${apt.employee.first_name} ${apt.employee.last_name}` : ''}
						</Typography>
					))}
					<Typography sx={{ fontSize: '12px', color: L.muted, mt: '2px', fontFamily: L.font }}>
						{getFullName(client)}
					</Typography>
				</Box>

				{/* Series check status */}
				<Box component="section">
					<LavSectionTitle>Series check</LavSectionTitle>
					{loadingConflicts ? (
						<CheckBar kind="loading">
							<CircularProgress size={13} sx={{ color: L.primary }} />
							<span>Checking the series for conflicts…</span>
						</CheckBar>
					) : conflictCount > 0 ? (
						<CheckBar kind="warn">
							<Icon name="warn" size={14} />
							<span>{conflictCount} of {activeVisits.length} visits {conflictCount === 1 ? 'has' : 'have'} a conflict — reschedule, skip, or allow</span>
						</CheckBar>
					) : (
						<CheckBar kind="ok">
							<Icon name="checkc" size={14} />
							<span>{allowedCount ? 'Conflicts resolved' : `All ${activeVisits.length} visits are clear`}{okParts.length ? ' · ' + okParts.join(' · ') : ''}</span>
						</CheckBar>
					)}
				</Box>

				{/* Occurrence list */}
				<Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
					{occurrences.map((o, idx) => (
						<VisitRow
							key={o.tempId}
							o={o}
							idx={idx}
							status={statusByOcc[o.tempId] || {}}
							conflictLines={conflictLinesFor(o)}
							duration={duration}
							expanded={expandedKeys.includes(o.tempId)}
							onToggle={() => toggleExpand(o.tempId)}
							onDateChange={(v) => handleDateChange(o.tempId, v)}
							onTimeChange={(v) => handleTimeChange(o.tempId, v)}
							onSkip={() => handleSkip(o.tempId)}
							onAllow={() => handleAllow(o.tempId)}
							onUndo={() => handleUndo(o.tempId)}
						/>
					))}
				</Box>
			</Box>

			{/* Footer */}
			<Box sx={{
				display: 'flex', flexDirection: 'column', gap: '12px',
				pt: '13px', pb: isMobile ? '0px' : '20px', px: isMobile ? 0 : '16px',
				mt: 'auto', bgcolor: L.bg,
			}}>
				{error && (
					<Box sx={{
						display: 'flex', alignItems: 'center', gap: '8px', p: '10px 12px', borderRadius: L.rSm,
						bgcolor: '#FDF3E4', border: '1.5px solid #F2DDB4', color: '#8F6217', fontSize: '12.5px',
						fontWeight: 650, fontFamily: L.font, cursor: 'pointer',
					}} onClick={() => setError('')}>
						<Box sx={{ flex: 'none', display: 'flex', color: '#C08A2D' }}><Icon name="warn" size={14} /></Box>
						<Box sx={{ flex: 1 }}>{error}</Box>
					</Box>
				)}

				{/* Total */}
				<Box sx={{
					display: 'flex', alignItems: 'center', gap: '8px',
					borderTop: `1.5px solid ${L.border}`, pt: '13px',
					px: isMobile ? '16px' : '0', fontSize: isMobile ? '15px' : '14px', fontFamily: L.font,
				}}>
					<Box component="span" sx={{ color: L.muted, fontWeight: 600 }}>
						{activeVisits.length} × {cs}{(pricePerOcc / 100).toFixed(2)}
					</Box>
					<Box component="strong" sx={{ ml: 'auto', fontSize: isMobile ? '17px' : '15.5px', fontWeight: 800, color: L.text, fontVariantNumeric: 'tabular-nums' }}>
						{cs}{(liveTotalPrice / 100).toFixed(2)}
					</Box>
				</Box>

				{/* Actions — mobile hides Back (the dialog header's back arrow covers it)
				    and shows the design's full-width 52px Book bar. */}
				<Box sx={{
					display: 'flex', gap: '10px', px: isMobile ? '16px' : '0',
					pb: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 20px)' : 0,
				}}>
					{!isMobile && (
						<Box
							component="button"
							type="button"
							onClick={onBack}
							disabled={booking}
							sx={{
								px: '16px', height: '42px', borderRadius: L.rSm,
								border: `1.5px solid ${L.border}`, bgcolor: 'transparent',
								fontSize: '14px', fontWeight: 650, color: L.muted, fontFamily: L.font, cursor: 'pointer', flex: 'none',
								'&:hover:not(:disabled)': { color: L.text, borderColor: L.muted },
								'&:disabled': { opacity: 0.5, cursor: 'default' },
							}}
						>
							Back
						</Box>
					)}
					<Box
						component="button"
						type="button"
						onClick={handleBook}
						disabled={!canBook}
						sx={{
							flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
							height: isMobile ? '52px' : '42px', px: '22px',
							borderRadius: isMobile ? '14px' : L.rSm, border: 'none',
							bgcolor: L.primary, color: L.onPrimary,
							fontSize: isMobile ? '15.5px' : '14px', fontWeight: isMobile ? 750 : 700,
							fontFamily: L.font, whiteSpace: 'nowrap', cursor: 'pointer',
							boxShadow: `0 2px 8px ${L.primarySoft}`,
							'&:hover:not(:disabled)': { bgcolor: L.primaryDeep },
							'&:disabled': { opacity: 0.45, cursor: 'default', boxShadow: 'none' },
						}}
					>
						{booking ? <CircularProgress size={16} sx={{ color: L.onPrimary }} /> : <Icon name="tick" size={15} />}
						<span>
							{booking
								? 'Booking…'
								: conflictCount > 0
									? 'Resolve conflicts to book'
									: `Book ${activeVisits.length} appointment${activeVisits.length === 1 ? '' : 's'}`}
						</span>
					</Box>
				</Box>
			</Box>
		</Box>
	)
}

// ---- status bar (amber warning / green all-clear / loading) ----
function CheckBar({ kind, children }) {
	const mobile = useLavMobile()
	const palette = {
		warn: { bg: '#FDF3E4', border: '#F2DDB4', color: '#8F6217', icon: '#C08A2D' },
		ok: { bg: '#E9F6EE', border: '#C9E8D4', color: '#25714A', icon: '#2F8A5B' },
		loading: { bg: L.surface, border: L.border, color: L.muted, icon: L.primary },
	}[kind]
	return (
		<Box sx={{
			display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
			borderRadius: mobile ? '12px' : L.rSm,
			p: mobile ? '12px 13px' : '10px 12px', fontSize: mobile ? '13px' : '12.5px',
			fontWeight: 650, fontFamily: L.font,
			border: `1.5px solid ${palette.border}`, bgcolor: palette.bg, color: palette.color,
			'& > svg': { color: palette.icon, flex: 'none' },
			'& > span': { flex: 1, minWidth: 0 },
		}}>
			{children}
		</Box>
	)
}

// ---- one visit row in the series check list ----
function VisitRow({ o, idx, status, conflictLines, duration, expanded, onToggle, onDateChange, onTimeChange, onSkip, onAllow, onUndo }) {
	const mobile = useLavMobile()
	const { hasConflict, unresolved, allowed } = status
	const start = o.time ? dayjs(`2000-01-01 ${o.time}`) : null
	const startLabel = start ? start.format('h:mm A') : '—'
	const endLabel = start ? start.add(duration, 'minute').format('h:mm A') : '—'
	const edited = o.date !== o.origDate || o.time !== o.origTime
	const openUp = idx >= 3

	// container variant
	const variant = o.removed
		? { border: `1.5px dashed ${L.border}`, bgcolor: L.surface }
		: unresolved
			? { border: '1.5px solid #F2DDB4', bgcolor: '#FFFBF3' }
			: allowed
				? { border: `1.5px solid ${L.primarySoft}`, bgcolor: '#FAF8FF' }
				: { border: `1.5px solid ${L.border}`, bgcolor: L.field }

	const dotColor = o.removed ? L.border : allowed ? L.primary : unresolved ? '#E0A33A' : '#3DA06D'

	return (
		<Box sx={{ ...variant, borderRadius: mobile ? '13px' : L.rSm, p: mobile ? '11px 12px' : '9px 11px', fontFamily: L.font }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
				<Box sx={{ width: '9px', height: '9px', flex: 'none', borderRadius: '50%', bgcolor: dotColor }} />
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography sx={{
						fontSize: mobile ? '14px' : '13px', fontWeight: 700, fontFamily: L.font,
						color: o.removed ? L.muted : L.text, textDecoration: o.removed ? 'line-through' : 'none',
					}}>
						{o.date ? dayjs(o.date).format('ddd, MMM D') : '—'}
					</Typography>
					<Typography sx={{ fontSize: mobile ? '12px' : '11.5px', color: L.muted, fontWeight: 600, mt: '1px', fontFamily: L.font }}>
						{o.removed
							? 'Skipped — not booked'
							: `${startLabel} – ${endLabel}${edited ? ' · moved' : ''}${allowed ? ' · overlap allowed' : ''}`}
					</Typography>
				</Box>
				{o.removed || allowed ? (
					<Box component="button" type="button" onClick={onUndo} sx={editLinkSx}>Undo</Box>
				) : unresolved ? null : (
					<Box
						component="button"
						type="button"
						aria-label="Edit this visit"
						onClick={onToggle}
						sx={{
							width: mobile ? 34 : 28, height: mobile ? 34 : 28, flex: 'none', border: 'none',
							borderRadius: mobile ? '10px' : '8px',
							display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
							bgcolor: expanded ? L.primarySoft : 'transparent', color: expanded ? L.primaryDeep : L.muted,
							'&:hover': { bgcolor: expanded ? L.primarySoft : L.surface, color: expanded ? L.primaryDeep : L.text },
						}}
					>
						<Icon name="pencil" size={14} />
					</Box>
				)}
			</Box>

			{/* Conflict detail lines */}
			{!o.removed && hasConflict && conflictLines.map((line, i) => (
				<Box key={i} sx={{
					display: 'flex', alignItems: 'center', gap: '7px',
					fontSize: mobile ? '12px' : '11.5px', fontWeight: 650,
					borderRadius: mobile ? '9px' : '8px', p: mobile ? '8px 10px' : '6px 9px', mt: '8px', fontFamily: L.font,
					color: allowed ? L.primaryDeep : '#8F6217', bgcolor: allowed ? L.primarySoft : '#FDF3E4',
					'& > svg': { color: allowed ? L.primary : '#C08A2D', flex: 'none' },
				}}>
					<Icon name={allowed ? 'checkc' : 'warn'} size={13} />
					<span>{allowed ? 'Double-booked with ' : 'Overlaps '}{line}</span>
				</Box>
			))}

			{/* Resolve buttons for unresolved conflicts */}
			{unresolved && (
				<Box sx={{ display: 'flex', gap: mobile ? '8px' : '7px', mt: mobile ? '10px' : '9px' }}>
					<ResolveBtn on={expanded} onClick={onToggle}>Reschedule</ResolveBtn>
					<ResolveBtn onClick={onSkip}>Skip</ResolveBtn>
					<ResolveBtn allow onClick={onAllow}>Allow overlap</ResolveBtn>
				</Box>
			)}

			{/* Inline date/time editor */}
			{!o.removed && !allowed && expanded && (
				<Box sx={{ display: 'flex', gap: '7px', mt: '8px', alignItems: 'flex-start' }}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<DropdownCalendar
							small
							up={openUp}
							value={o.date}
							onChange={onDateChange}
							min={dayjs().format('YYYY-MM-DD')}
						/>
					</Box>
					<Box sx={{ width: '128px', flex: 'none' }}>
						<TimeListPicker small up={openUp} align="right" step={SLOT_INTERVAL_MINUTES} value={o.time} onChange={onTimeChange} />
					</Box>
					{!unresolved && (
						<Box
							component="button"
							type="button"
							onClick={onSkip}
							sx={{
								display: 'flex', alignItems: 'center', gap: '5px',
								height: mobile ? '44px' : '36px', px: '11px', flex: 'none',
								border: `1.5px solid ${L.border}`, borderRadius: mobile ? '11px' : L.rSm, bgcolor: L.field,
								fontSize: mobile ? '12.5px' : '12px', fontWeight: 700, color: L.muted, fontFamily: L.font, cursor: 'pointer',
								'&:hover': { color: L.text, borderColor: L.muted },
							}}
						>
							<Icon name="x" size={12} /><span>Skip</span>
						</Box>
					)}
				</Box>
			)}
		</Box>
	)
}

function ResolveBtn({ on, allow, onClick, children }) {
	const mobile = useLavMobile()
	return (
		<Box
			component="button"
			type="button"
			onClick={onClick}
			sx={{
				flex: '1 1 0', minWidth: 0, height: mobile ? '42px' : '34px', px: '6px', whiteSpace: 'nowrap',
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				border: `1.5px solid ${on ? L.primary : L.border}`, borderRadius: mobile ? '11px' : L.rSm,
				bgcolor: on ? L.primarySoft : L.field, fontSize: mobile ? '12.5px' : '12px', fontWeight: 700, fontFamily: L.font,
				color: on ? L.primaryDeep : L.muted, cursor: 'pointer',
				transition: 'border-color .12s, background .12s, color .12s',
				'&:hover': allow
					? { borderColor: L.primary, bgcolor: L.primarySoft, color: L.primaryDeep }
					: { color: L.text, borderColor: L.muted },
			}}
		>
			{children}
		</Box>
	)
}

const editLinkSx = {
	border: 'none', background: 'transparent', color: L.primaryDeep, fontSize: '13px', fontWeight: 700,
	p: '6px 8px', borderRadius: '8px', flex: 'none', cursor: 'pointer', fontFamily: L.font,
	'&:hover': { bgcolor: L.primarySoft },
}
